import { formatDistanceStrict } from 'date-fns';

const MILLISECONDS_PER_DAY = 86400000;
const MILLISECONDS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;
const HOURS_OFFSET_FROM_UTC = 3;
/**
 * Calculates a date based on 3 hours ahead of UTC.
 * @param {Object} [options] - The options object.
 * @param {number} [options.daysAgo=0] - Number of days to subtract from the current date.
 * @param {Date} [options.now=new Date()] - The base date to use for calculations.
 * @returns {string} The formatted date string in 'YYYY-MM-DD' format.
 */
function getDate(options) {
  const { daysAgo = 0, now = new Date() } = options || {};

  const offsetDate = new Date(now.getTime() + shiftTimeInMilliseconds(now) - daysAgo * MILLISECONDS_PER_DAY);

  return formatDate(offsetDate);
}

/**
 * Formats a Date object into a 'YYYY-MM-DD' string.
 * @param {Date} date - The date to format.
 * @returns {string} The formatted date string.
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Calculates the time shift in milliseconds based on 3 hours ahead of UTC.
 * This ensures that if the time is before 3AM UTC, it is considered the previous day.
 * @param {Date} now - The current date and time.
 * @returns {number} The time shift in milliseconds.
 */
function shiftTimeInMilliseconds(now) {
  const timeShiftInMinutes = (now.getTimezoneOffset() - HOURS_OFFSET_FROM_UTC * MINUTES_PER_HOUR);
  return timeShiftInMinutes * MILLISECONDS_PER_MINUTE;
}

/**
 * Describes how long ago a timestamp was, e.g. '5 minutes ago' or 'just now'.
 * @param {string|Date} timestamp - The past time.
 * @param {Date} [now=new Date()] - The time to measure from.
 * @returns {string} The relative time.
 */
function timeAgo(timestamp, now = new Date()) {
  const date = new Date(timestamp);
  if (now - date < MILLISECONDS_PER_MINUTE) {
    return 'just now';
  }
  return `${formatDistanceStrict(date, now)} ago`;
}

export { getDate, formatDate, timeAgo };
